import { useState, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { X, Search } from 'lucide-react';

export default function ClienteMultiSelect({ clientes, selectedIds, onChange }) {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredClientes = useMemo(() => {
    // Garante que 'clientes' é um array
    if (!Array.isArray(clientes)) return []; 
    
    const lowerSearchTerm = searchTerm.toLowerCase().trim();
    if (!lowerSearchTerm) return clientes; // Retorna todos se a busca estiver vazia

    const searchTermNumbers = searchTerm.replace(/\D/g, ''); // Para busca por CPF

    return clientes.filter((c) => {
        // Verifica se 'c' existe e se os campos existem antes de tentar aceder
        if (!c) return false; 
        
        // Verifica nome (case-insensitive)
        const nameMatch = c.nome && typeof c.nome === 'string' && c.nome.toLowerCase().includes(lowerSearchTerm);
        
        // Verifica CPF (apenas números)
        const cpfMatch = searchTermNumbers && c.cpf && typeof c.cpf === 'string' && c.cpf.includes(searchTermNumbers);
        
        // Verifica email (case-insensitive)
        const emailMatch = c.email && typeof c.email === 'string' && c.email.toLowerCase().includes(lowerSearchTerm);

        return nameMatch || cpfMatch || emailMatch;
      }
    );
  }, [clientes, searchTerm]);

  const selectedClientes = useMemo(() => {
    // Garante que 'clientes' é um array
    if (!Array.isArray(clientes)) return [];
    // Garante que selectedIds é um array
    const validSelectedIds = Array.isArray(selectedIds) ? selectedIds : []; 
    return clientes.filter((c) => c && c.id && validSelectedIds.includes(c.id)); // Verifica se 'c' e 'c.id' existem
  }, [clientes, selectedIds]);

  const toggleCliente = (clienteId) => {
    // Garante que selectedIds é um array
    const validSelectedIds = Array.isArray(selectedIds) ? selectedIds : []; 
    if (validSelectedIds.includes(clienteId)) {
      onChange(validSelectedIds.filter((id) => id !== clienteId));
    } else {
      onChange([...validSelectedIds, clienteId]);
    }
  };

  const removeCliente = (clienteId) => {
     // Garante que selectedIds é um array
    const validSelectedIds = Array.isArray(selectedIds) ? selectedIds : [];
    onChange(validSelectedIds.filter((id) => id !== clienteId));
  };

  // Garante que selectedIds é um array para a verificação final
  const validSelectedIds = Array.isArray(selectedIds) ? selectedIds : [];

  return (
    <div className="space-y-3">
      <Label>Clientes</Label>
      
      {/* Selected Clients Display */}
      {selectedClientes.length > 0 && (
        <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
          {selectedClientes.map((cliente) => (
            <Badge
              key={cliente.id}
              data-testid={`selected-client-${cliente.id}`}
              variant="secondary"
              className="px-3 py-1 flex items-center space-x-2"
            >
              {/* Adiciona verificação para nome */}
              <span>{cliente.nome || 'Nome Indisponível'}</span> 
              <button
                type="button"
                onClick={() => removeCliente(cliente.id)}
                className="hover:text-red-600 transition-colors"
                aria-label={`Remover ${cliente.nome || 'cliente'}`}
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          data-testid="client-search-input"
          type="text"
          placeholder="Buscar por nome, CPF ou email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Client List */}
      <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
        {filteredClientes.length === 0 ? (
          <div className="p-4 text-center text-gray-500 text-sm">
            Nenhum cliente encontrado
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredClientes.map((cliente) => (
              <div
                // Adiciona verificação para garantir que cliente e cliente.id existem
                key={cliente?.id || Math.random()} // Usa chave aleatória como fallback se id faltar
                data-testid={`client-option-${cliente?.id}`}
                onClick={() => cliente?.id && toggleCliente(cliente.id)} // Só permite clique se ID existir
                className={`p-3 cursor-pointer transition-colors hover:bg-gray-50 ${
                  cliente?.id && validSelectedIds.includes(cliente.id) ? 'bg-blue-50' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    {/* Adiciona verificações para nome, cpf, email */}
                    <p className="font-medium text-gray-900">{cliente?.nome || 'Nome Indisponível'}</p>
                    <p className="text-sm text-gray-500">
                        {cliente?.cpf ? cliente.cpf : 'CPF Indisponível'} 
                        {cliente?.email ? ` • ${cliente.email}` : ''}
                    </p>
                  </div>
                  {cliente?.id && validSelectedIds.includes(cliente.id) && (
                    <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Garante que validSelectedIds existe antes de verificar length */}
      {validSelectedIds.length === 0 && (
        <p className="text-sm text-red-600">Selecione pelo menos um cliente</p>
      )}
    </div>
  );
}
